<template>
  <div class="bg-gray-50 min-h-screen">
    <AlertMessage
      v-if="alert.message"
      :message="alert.message"
      :alert-type="alert.type"
      @close="clearAlert"
    />

    <div class="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div
        class="flex flex-col md:flex-row md:items-start md:justify-between gap-4"
      >
        <div class="space-y-2">
          <h1 class="text-2xl font-semibold text-neutral-text">
            Organization Settings
          </h1>
          <p class="text-neutral-light max-w-2xl">
            Keep your organization profile and contact information up to date so
            teammates and integrations know who they are working with.
          </p>
        </div>
      </div>

      <div
        v-if="!hasAdminToken"
        class="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 flex flex-col gap-3"
      >
        <div class="flex items-start gap-3">
          <div
            class="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-amber-100 text-amber-600"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-5 h-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <div class="space-y-1">
            <h2 class="text-sm font-semibold text-amber-900">
              Finish setting up your workspace
            </h2>
            <p class="text-sm text-amber-700">
              Onboarding is not complete. Complete it to manage spaces and
              unlock the full application functionality.
            </p>
          </div>
        </div>
        <div>
          <button
            type="button"
            @click="redirectToTokenCreation"
            class="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Complete Onboarding
          </button>
        </div>
      </div>

      <div class="bg-white border border-gray-100 rounded-3xl shadow-sm">
        <div class="px-6 py-5 border-b border-gray-100">
          <h2 class="text-lg font-semibold text-neutral-text">
            Organization Profile
          </h2>
          <p class="text-sm text-neutral-light mt-1">
            Update branding details, location and contact preferences.
          </p>
        </div>

        <div class="p-6">
          <div v-if="isLoading" class="py-20 flex justify-center">
            <div
              class="w-10 h-10 border-2 border-gray-200 border-t-primary rounded-full animate-spin"
            ></div>
          </div>

          <form v-else class="space-y-10" @submit.prevent="submitForm">
            <section class="space-y-6">
              <div>
                <label
                  for="organization-name"
                  class="block text-sm font-medium text-neutral-text mb-2"
                >
                  Organization Name
                </label>
                <input
                  id="organization-name"
                  v-model="organizationForm.name"
                  type="text"
                  @input="clearError('name')"
                  class="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-neutral-text focus:border-primary focus:ring-2 focus:ring-primary"
                  :class="{
                    'border-red-300 focus:border-red-400 focus:ring-red-100':
                      errors.name,
                  }"
                  placeholder="Acme Inc."
                  autocomplete="organization"
                />
                <p v-if="errors.name" class="mt-2 text-sm text-red-500">
                  {{ errors.name }}
                </p>
              </div>

              <div class="space-y-4">
                <p class="text-sm font-semibold text-neutral-text">
                  Organization Logo
                </p>
                <div class="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div
                    class="h-20 w-20 flex items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-gray-50 overflow-hidden"
                  >
                    <img
                      v-if="organizationImageUrl"
                      :src="organizationImageUrl"
                      alt="Organization logo"
                      class="h-full w-full object-cover"
                    />
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-8 w-8 text-gray-300"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 9.75L12 14.25m0 0L7.5 9.75M12 14.25V3"
                      />
                    </svg>
                  </div>
                  <div class="flex-1">
                    <label
                      for="organization-logo"
                      class="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-neutral-text shadow-sm transition hover:border-primary hover:text-primary"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 4.5v15m7.5-7.5h-15"
                        />
                      </svg>
                      Upload Image
                    </label>
                    <input
                      id="organization-logo"
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      class="hidden"
                      @change="handleFileUpload"
                    />
                    <p class="mt-2 text-xs text-neutral-light">
                      JPG or PNG up to 2MB. A square image works best.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section class="space-y-6">
              <h3
                class="text-sm font-semibold text-neutral-text uppercase tracking-wide"
              >
                Location & Industry
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label
                    for="organization-country"
                    class="block text-sm font-medium text-neutral-text mb-2"
                  >
                    Country
                  </label>
                  <select
                    id="organization-country"
                    v-model="organizationForm.country"
                    @change="clearError('country')"
                    class="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-neutral-text focus:border-primary focus:ring-2 focus:ring-primary"
                    :class="{
                      'border-red-300 focus:border-red-400 focus:ring-red-100':
                        errors.country,
                    }"
                  >
                    <option value="">Select a country</option>
                    <option
                      v-for="countryOption in countriesList"
                      :key="countryOption"
                      :value="countryOption"
                    >
                      {{ countryOption }}
                    </option>
                  </select>
                  <p v-if="errors.country" class="mt-2 text-sm text-red-500">
                    {{ errors.country }}
                  </p>
                </div>

                <div>
                  <label
                    for="organization-city"
                    class="block text-sm font-medium text-neutral-text mb-2"
                  >
                    City
                  </label>
                  <input
                    id="organization-city"
                    v-model="organizationForm.city"
                    type="text"
                    @input="clearError('city')"
                    class="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-neutral-text focus:border-primary focus:ring-2 focus:ring-primary"
                    :class="{
                      'border-red-300 focus:border-red-400 focus:ring-red-100':
                        errors.city,
                    }"
                    placeholder="New York"
                    autocomplete="address-level2"
                  />
                  <p v-if="errors.city" class="mt-2 text-sm text-red-500">
                    {{ errors.city }}
                  </p>
                </div>

                <div>
                  <label
                    for="organization-address"
                    class="block text-sm font-medium text-neutral-text mb-2"
                  >
                    Address
                  </label>
                  <input
                    id="organization-address"
                    v-model="organizationForm.address"
                    type="text"
                    @input="clearError('address')"
                    class="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-neutral-text focus:border-primary focus:ring-2 focus:ring-primary"
                    :class="{
                      'border-red-300 focus:border-red-400 focus:ring-red-100':
                        errors.address,
                    }"
                    placeholder="123 Main Street"
                    autocomplete="street-address"
                  />
                  <p v-if="errors.address" class="mt-2 text-sm text-red-500">
                    {{ errors.address }}
                  </p>
                </div>

                <div>
                  <label
                    for="organization-industry"
                    class="block text-sm font-medium text-neutral-text mb-2"
                  >
                    Industry
                  </label>
                  <select
                    id="organization-industry"
                    v-model="organizationForm.industry"
                    @change="clearError('industry')"
                    class="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-neutral-text focus:border-primary focus:ring-2 focus:ring-primary"
                    :class="{
                      'border-red-300 focus:border-red-400 focus:ring-red-100':
                        errors.industry,
                    }"
                  >
                    <option value="">Select an industry</option>
                    <option
                      v-for="industryOption in industriesList"
                      :key="industryOption"
                      :value="industryOption"
                    >
                      {{ industryOption }}
                    </option>
                  </select>
                  <p v-if="errors.industry" class="mt-2 text-sm text-red-500">
                    {{ errors.industry }}
                  </p>
                </div>
              </div>
            </section>

            <section class="space-y-6">
              <h3
                class="text-sm font-semibold text-neutral-text uppercase tracking-wide"
              >
                Contact
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label
                    for="organization-email"
                    class="block text-sm font-medium text-neutral-text mb-2"
                  >
                    Company Email
                  </label>
                  <input
                    id="organization-email"
                    v-model="organizationForm.email"
                    type="email"
                    @input="clearError('email')"
                    class="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-neutral-text focus:border-primary focus:ring-2 focus:ring-primary"
                    :class="{
                      'border-red-300 focus:border-red-400 focus:ring-red-100':
                        errors.email,
                    }"
                    placeholder="hello@acme.com"
                    autocomplete="email"
                  />
                  <p v-if="errors.email" class="mt-2 text-sm text-red-500">
                    {{ errors.email }}
                  </p>
                </div>

                <div>
                  <label
                    for="organization-phone"
                    class="block text-sm font-medium text-neutral-text mb-2"
                  >
                    Phone Number (Optional)
                  </label>
                  <input
                    id="organization-phone"
                    v-model="organizationForm.phoneNumber"
                    type="tel"
                    @input="clearError('phoneNumber')"
                    class="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-neutral-text focus:border-primary focus:ring-2 focus:ring-primary"
                    :class="{
                      'border-red-300 focus:border-red-400 focus:ring-red-100':
                        errors.phoneNumber,
                    }"
                    placeholder="+1 555 000 0000"
                    autocomplete="tel"
                  />
                  <p
                    v-if="errors.phoneNumber"
                    class="mt-2 text-sm text-red-500"
                  >
                    {{ errors.phoneNumber }}
                  </p>
                </div>
              </div>
            </section>

            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3"
            >
              <button
                type="submit"
                class="inline-flex items-center justify-center rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="isSubmitting"
              >
                <svg
                  v-if="isSubmitting"
                  class="-ml-1 mr-2 h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import AlertMessage from "@/components/AlertMessage.vue";
import { getOrganizations, updateOrganization } from "@/services/organization";
import countries from "@/assets/data/countries";
import industries from "@/assets/data/industries";

const organizationForm = reactive({
  name: "",
  country: "",
  city: "",
  address: "",
  industry: "",
  email: "",
  phoneNumber: "",
});

const errors = reactive({
  name: "",
  country: "",
  city: "",
  address: "",
  industry: "",
  email: "",
  phoneNumber: "",
});

const alert = reactive({
  message: "",
  type: "",
});

const organizationId = ref("");
const hasAdminToken = ref(false);
const isLoading = ref(true);
const isSubmitting = ref(false);
const organizationImageUrl = ref("");
const organizationImageFile = ref(null);
const hasNewImage = ref(false);
const countriesList = countries;
const industriesList = industries;

onMounted(() => {
  loadOrganizationData();
});

const loadOrganizationData = async () => {
  isLoading.value = true;
  try {
    const orgData = await getOrganizations();
    organizationId.value = orgData?.id || "";
    organizationForm.name = orgData?.name || "";
    organizationForm.country = orgData?.country || "";
    organizationForm.city = orgData?.city || "";
    organizationForm.address = orgData?.address || "";
    organizationForm.industry = orgData?.industry || "";
    organizationForm.email = orgData?.email || "";
    organizationForm.phoneNumber = orgData?.phoneNumber || "";
    organizationImageUrl.value = orgData?.image || "";
    hasAdminToken.value = Boolean(orgData?.hasAdminToken);
  } catch (error) {
    setAlert("Failed to load organization data.", "dismiss");
  } finally {
    isLoading.value = false;
  }
};

const clearAlert = () => {
  alert.message = "";
  alert.type = "";
};

const setAlert = (message, type = "dismiss") => {
  alert.message = message;
  alert.type = type;
};

const clearError = (field) => {
  errors[field] = "";
};

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return true;
  }
  const phoneRegex = /^[0-9+()\-\s]{6,}$/;
  return phoneRegex.test(phoneNumber.trim());
};

const validateForm = () => {
  let hasErrors = false;

  if (!organizationForm.name.trim()) {
    errors.name = "Organization name is required.";
    hasErrors = true;
  }

  if (!organizationForm.country) {
    errors.country = "Country is required.";
    hasErrors = true;
  }

  if (!organizationForm.city.trim()) {
    errors.city = "City is required.";
    hasErrors = true;
  }

  if (!organizationForm.address.trim()) {
    errors.address = "Address is required.";
    hasErrors = true;
  }

  if (!organizationForm.industry) {
    errors.industry = "Industry is required.";
    hasErrors = true;
  }

  if (!validateEmail(organizationForm.email)) {
    errors.email = "Please enter a valid email.";
    hasErrors = true;
  }

  if (!validatePhoneNumber(organizationForm.phoneNumber)) {
    errors.phoneNumber = "Please enter a valid phone number.";
    hasErrors = true;
  }

  return !hasErrors;
};

const readImageAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === "string") {
        resolve(result.split(",")[1]);
      } else {
        reject(new Error("Unable to read file"));
      }
    };
    reader.onerror = () => reject(new Error("Unable to read file"));
    reader.readAsDataURL(file);
  });
};

const handleFileUpload = (event) => {
  const file = event.target.files?.[0];
  if (!file) {
    return;
  }

  organizationImageFile.value = file;
  hasNewImage.value = true;

  if (
    organizationImageUrl.value &&
    organizationImageUrl.value.startsWith("blob:")
  ) {
    URL.revokeObjectURL(organizationImageUrl.value);
  }

  organizationImageUrl.value = URL.createObjectURL(file);
  event.target.value = "";
};

const submitForm = async () => {
  clearAlert();

  // Reset errors before validating
  Object.keys(errors).forEach((key) => {
    errors[key] = "";
  });

  if (!validateForm()) {
    return;
  }

  const payload = {
    name: organizationForm.name.trim(),
    city: organizationForm.city.trim(),
    address: organizationForm.address.trim(),
    country: organizationForm.country,
    industry: organizationForm.industry,
    email: organizationForm.email.trim(),
    phoneNumber: organizationForm.phoneNumber
      ? organizationForm.phoneNumber.trim()
      : null,
    image: null,
  };

  if (hasNewImage.value && organizationImageFile.value) {
    try {
      payload.image = await readImageAsBase64(organizationImageFile.value);
    } catch (error) {
      setAlert("Unable to process image. Please try a different file.");
      return;
    }
  }

  isSubmitting.value = true;
  try {
    await updateOrganization(organizationId.value, JSON.stringify(payload));
    setAlert("Organization updated successfully!", "success");
    hasNewImage.value = false;
    organizationImageFile.value = null;
  } catch (error) {
    setAlert("Failed to update organization.", "dismiss");
  } finally {
    isSubmitting.value = false;
  }
};

const redirectToTokenCreation = () => {
  const url = process.env.ONBOARDING_CONNECT_URL;
  if (!url) {
    setAlert("Onboarding link is not configured.");
    return;
  }
  window.location.href = url;
};

onBeforeUnmount(() => {
  if (
    organizationImageUrl.value &&
    organizationImageUrl.value.startsWith("blob:")
  ) {
    URL.revokeObjectURL(organizationImageUrl.value);
  }
});
</script>

<style scoped>
:deep(.alert-container) {
  margin-left: 0;
  padding-right: 0;
}
</style>
